import {
  Paper,
  Typography,
  Divider,
  Box,
  Tooltip,
  IconButton,
  Popper,
  ClickAwayListener,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { Cart } from '../../../app/data/models/cart';
import { formatCurrency } from '../../../app/utils/formatters';
import { OrderType } from '../../../app/data/models/orders/orderType';
import { Info } from '@mui/icons-material';
import { useState } from 'react';

interface CartOrderSummaryProps {
  cart: Cart;
  status: string;
  tableNumber: number | undefined;
  orderType: OrderType;
  onCheckout: () => void;
  priceIncludesTax: boolean;
}

const TaxBreakdownTooltip = ({
  cart,
  priceIncludesTax,
}: {
  cart: Cart;
  priceIncludesTax: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        {t('cart.taxBreakdown')}
      </Typography>
      {cart.orderItems.map((item) => {
        const taxRate = item.taxRate ?? cart.defaultTaxRate ?? 0;
        const itemTotal = item.totalPrice;
        const taxAmount = priceIncludesTax
          ? itemTotal - itemTotal / (1 + taxRate / 100)
          : itemTotal * (taxRate / 100);

        return (
          <Box
            key={item.cartItemId}
            sx={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <Typography variant="body2">
              {item.name} (x{item.quantity}) @ {taxRate}%
            </Typography>
            <Typography variant="body2" sx={{ ml: 2 }}>
              {formatCurrency(taxAmount)}
            </Typography>
          </Box>
        );
      })}
    </Paper>
  );
};

export const CartOrderSummary = ({
  cart,
  status,
  tableNumber,
  orderType,
  onCheckout,
  priceIncludesTax,
}: CartOrderSummaryProps) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleClickAway = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'tax-breakdown-popper' : undefined;

  const hasMultipleTaxRates =
    new Set(cart.orderItems.map((item) => item.taxRate ?? cart.defaultTaxRate))
      .size > 1;

  const subtotal = priceIncludesTax
    ? cart.totalPrice - cart.totalTax
    : cart.subtotal;

  return (
    <Paper
      elevation={2}
      sx={{
        width: { xs: '100%', md: '100%' },
        padding: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Typography variant="h5" gutterBottom>
        {t('cart.order.summary')}
      </Typography>

      <Box sx={{ mb: 2 }}>
        {orderType === OrderType.DineIn && tableNumber && (
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('table.title')} #{tableNumber}
          </Typography>
        )}
        <Typography variant="body2">
          {cart.orderItems.length} {t('cart.items')}
        </Typography>
      </Box>

      <Divider />

      <Box sx={{ my: 2 }}>
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Typography variant="body1">{t('cart.subtotal')}</Typography>
          <Typography variant="body1">{formatCurrency(subtotal)}</Typography>
        </Box>
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Box display="flex" alignItems="center">
            <Typography variant="body1">{t('cart.tax')}</Typography>
            {hasMultipleTaxRates ? (
              <ClickAwayListener onClickAway={handleClickAway}>
                <div>
                  <IconButton
                    size="small"
                    onClick={handleClick}
                    aria-describedby={id}
                  >
                    <Info fontSize="small" />
                  </IconButton>
                  <Popper
                    id={id}
                    open={open}
                    anchorEl={anchorEl}
                    placement="top-start"
                  >
                    <TaxBreakdownTooltip
                      cart={cart}
                      priceIncludesTax={priceIncludesTax}
                    />
                  </Popper>
                </div>
              </ClickAwayListener>
            ) : (
              <Typography variant="body1" sx={{ ml: 0.5 }}>
                ({cart.defaultTaxRate}%)
              </Typography>
            )}
          </Box>
          <Typography variant="body1">
            {formatCurrency(cart.totalTax)}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />
        <Box display="flex" justifyContent="space-between" mb={2}>
          <Typography variant="h6">{t('cart.order.total')}</Typography>
          <Typography variant="h6">
            {formatCurrency(cart.totalPrice)}
          </Typography>
        </Box>
      </Box>

      <Divider />

      <LoadingButton
        fullWidth
        size="large"
        loading={status.includes('pending')}
        type="submit"
        variant="contained"
        color="primary"
        disabled={cart.orderItems.length === 0}
        onClick={onCheckout}
      >
        {t('cart.order.place-order')}
      </LoadingButton>
    </Paper>
  );
};
